import React, { useMemo, useState } from 'react';
import { HbrDomTable, HbrPagination } from '@harbor/elements-react-standalone';
import { useNavigate } from 'react-router-dom';

import { useMoodleTeamEventsByWindow } from '../../../hooks/useMoodleEventBooking';
import { useMoodleCourses } from '../../../hooks/useMoodleHooks';
import { buildCourseDetailPath, buildEventDetailPath } from '../../../support/moodleDetailRoutes';

const PAGE_SIZE_OPTIONS = [30, 50, 100];
const DAYS = [7, 30, 90, 180, 365] as const;
const LEADER_RETURN_TO = '/leader';

type TimePreset =
  | 'past_7'
  | 'past_30'
  | 'past_90'
  | 'past_180'
  | 'past_365'
  | 'future_7'
  | 'future_30'
  | 'future_90'
  | 'future_180'
  | 'future_365';

interface Props {
  teamEmails: string[];
  onSelectPerson: (email: string) => void;
}

type Row = {
  id: string;
  event_id: number;
  title: string;
  event_date?: string | null;
  event_end_date?: string | null;
  course_id: number | null;
  course_label: string;
  owners_team: string[];
  proctors_team: string[];
};

type PillTone = 'success' | 'info' | 'neutral' | 'warning';

function safeId(n: unknown) {
  return `ev_${String(n).replace(/[^a-z0-9_-]/gi, '_')}`;
}

function pillStyle(kind: PillTone): React.CSSProperties {
  const map: Record<PillTone, React.CSSProperties> = {
    success: {
      background: 'var(--hbr-color-success-bg, #e8f5e9)',
      border: '1px solid var(--hbr-color-success-border, #a5d6a7)',
      color: 'var(--hbr-color-success-foreground, #1b5e20)',
    },
    info: {
      background: 'var(--hbr-color-info-bg, #e3f2fd)',
      border: '1px solid var(--hbr-color-info-border, #90caf9)',
      color: 'var(--hbr-color-info-foreground, #0d47a1)',
    },
    neutral: {
      background: 'var(--hbr-color-neutral-bg-medium, #f3f4f6)',
      border: '1px solid var(--hbr-color-neutral-border-weak, #d1d5db)',
      color: 'var(--hbr-color-text-primary, #111827)',
    },
    warning: {
      background: 'var(--hbr-color-warning-bg, #fff8e1)',
      border: '1px solid var(--hbr-color-warning-border, #ffe082)',
      color: 'var(--hbr-color-warning-foreground, #8d6e63)',
    },
  };

  return {
    ...map[kind],
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 999,
    padding: '2px 10px',
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.5,
    whiteSpace: 'nowrap',
  };
}

function metricCard(label: string, value: React.ReactNode, sublabel?: React.ReactNode) {
  return (
    <div
      className="hbr-css__container-card"
      style={{
        padding: 12,
        minHeight: 84,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 4,
      }}
    >
      <div className="hbr-css__text-secondary-xs">{label}</div>
      <div className="hbr-css__text-primary-md-semibold">{value}</div>
      {sublabel ? <div className="hbr-css__text-secondary-xs">{sublabel}</div> : null}
    </div>
  );
}

function parseCourseIdFromEvent(e: any): number | null {
  const courseVal = e?.params?.joomdle_courses;
  if (Array.isArray(courseVal)) {
    const n = Number(courseVal?.[0]);
    return Number.isFinite(n) ? n : null;
  }
  const n = Number(courseVal);
  return Number.isFinite(n) ? n : null;
}

function formatSqlDatetimeLocal(d: Date) {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function computeWindowFromPreset(preset: TimePreset) {
  const [dir, daysStr] = preset.split('_') as ['past' | 'future', string];
  const days = Number(daysStr);
  const now = new Date();

  if (dir === 'past') {
    const min = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    return { min: formatSqlDatetimeLocal(min), max: formatSqlDatetimeLocal(now) };
  } else {
    const max = new Date(now.getTime() + days * 24 * 60 * 60 * 1000);
    return { min: formatSqlDatetimeLocal(now), max: formatSqlDatetimeLocal(max) };
  }
}

export default function WorkshopsTeamEventsTable({ teamEmails, onSelectPerson }: Props) {
  const navigate = useNavigate();

  const { data: courses = [] } = useMoodleCourses({
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const courseNameById = useMemo(() => {
    const m = new Map<number, string>();
    (Array.isArray(courses) ? courses : []).forEach((c: any) => {
      const id = Number(c?.id);
      const name = String(c?.fullname ?? c?.shortname ?? '').trim();
      if (Number.isFinite(id) && name) m.set(id, name);
    });
    return m;
  }, [courses]);

  const [timePreset, setTimePreset] = useState<TimePreset>('past_90');
  const { min, max } = useMemo(() => computeWindowFromPreset(timePreset), [timePreset]);

  const [selectedUser, setSelectedUser] = useState<string>('');
  const [selectedCourseId, setSelectedCourseId] = useState<string>('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(30);
  const apiPage = pageIndex + 1;

  const { data, isLoading, error } = useMoodleTeamEventsByWindow(
    teamEmails,
    apiPage,
    pageSize,
    min,
    max,
    selectedCourseId ? selectedCourseId : undefined
  );

  const events = useMemo(() => data?.items ?? [], [data]);
  const totalCount = useMemo(() => {
    if (typeof data?.total_count === 'number') return data.total_count;
    if (typeof data?.returned === 'number') return data.returned;
    return events.length;
  }, [data, events.length]);

  const rows: Row[] = useMemo(() => {
    return (events || [])
      .map((e: any) => {
        const cid = parseCourseIdFromEvent(e);
        const course_label = cid ? courseNameById.get(cid) ?? `Course ${cid}` : '—';

        const tr = e?.team_roles ?? {};
        const owners_team: string[] = Array.isArray(tr.owners)
          ? tr.owners.map((x: any) => String(x).toLowerCase()).filter(Boolean)
          : [];
        const proctors_team: string[] = Array.isArray(tr.proctors)
          ? tr.proctors.map((x: any) => String(x).toLowerCase()).filter(Boolean)
          : [];

        return {
          id: safeId(e.id),
          event_id: Number(e.id),
          title: String(e.title ?? ''),
          event_date: e.event_date ?? null,
          event_end_date: e.event_end_date ?? null,
          course_id: cid,
          course_label,
          owners_team,
          proctors_team,
        };
      })
      .filter((r) => r.owners_team.length > 0 || r.proctors_team.length > 0);
  }, [events, courseNameById]);

  const courseOptions = useMemo(() => {
    const set = new Set<number>();
    for (const r of rows) {
      if (r.course_id) set.add(r.course_id);
    }
    return Array.from(set)
      .sort((a, b) => a - b)
      .map((id) => ({ id, label: courseNameById.get(id) ?? `Course ${id}` }));
  }, [rows, courseNameById]);

  const filteredRows = useMemo(() => {
    let out = rows;

    const u = selectedUser.trim().toLowerCase();
    if (u) {
      out = out.filter((r) => r.owners_team.includes(u) || r.proctors_team.includes(u));
    }

    if (selectedCourseId) {
      const cid = Number(selectedCourseId);
      out = out.filter((r) => r.course_id === cid);
    }

    return out;
  }, [rows, selectedUser, selectedCourseId]);

  const summary = useMemo(() => {
    const eventCount = filteredRows.length;
    const ownerInvolvement = filteredRows.filter((r) => r.owners_team.length > 0).length;
    const proctorInvolvement = filteredRows.filter((r) => r.proctors_team.length > 0).length;
    const uniqueCourses = new Set(
      filteredRows.map((r) => r.course_id).filter((v): v is number => Number.isFinite(v as number))
    ).size;

    return {
      eventCount,
      ownerInvolvement,
      proctorInvolvement,
      uniqueCourses,
    };
  }, [filteredRows]);

  if (!teamEmails.length) {
    return <div className="hbr-css__text-secondary-sm">No team emails found.</div>;
  }

  if (isLoading) {
    return <div className="hbr-css__text-secondary-sm">Loading team events…</div>;
  }

  if (error) {
    return <div style={{ color: 'red' }}>{(error as Error).message}</div>;
  }

  return (
    <div className="flex flex-col gap-3 h-full min-h-0">
      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}
      >
        {metricCard('Events', summary.eventCount)}
        {metricCard('Courses Represented', summary.uniqueCourses)}
        {metricCard('Owner Participation', summary.ownerInvolvement)}
        {metricCard('Proctor Participation', summary.proctorInvolvement)}
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <select
          className="border rounded px-2 py-1 text-sm"
          value={timePreset}
          onChange={(e) => {
            setTimePreset(e.target.value as TimePreset);
            setPageIndex(0);
          }}
        >
          {DAYS.map((d) => (
            <option key={`p-${d}`} value={`past_${d}` as TimePreset}>
              Past {d} days
            </option>
          ))}
          {DAYS.map((d) => (
            <option key={`f-${d}`} value={`future_${d}` as TimePreset}>
              Future {d} days
            </option>
          ))}
        </select>

        <select
          className="border rounded px-2 py-1 text-sm"
          value={selectedUser}
          onChange={(e) => {
            setSelectedUser(e.target.value);
            setPageIndex(0);
          }}
        >
          <option value="">All team members</option>
          {teamEmails
            .map((e) => e.toLowerCase())
            .sort()
            .map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
        </select>

        <select
          className="border rounded px-2 py-1 text-sm"
          value={selectedCourseId}
          onChange={(e) => {
            setSelectedCourseId(e.target.value);
            setPageIndex(0);
          }}
        >
          <option value="">All courses</option>
          {courseOptions.map((c) => (
            <option key={c.id} value={String(c.id)}>
              {c.label} ({c.id})
            </option>
          ))}
        </select>
      </div>

      <div className="hbr-css__text-secondary-xs">
        Window uses event <b>end date</b>: {min} → {max}
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <HbrDomTable
          autoLayout
          style={{ display: 'block', width: '100%' }}
          columns={[
            { id: 'event', name: 'Event', width: '26%' },
            { id: 'course', name: 'Course', width: '22%' },
            { id: 'owner', name: 'Owners', width: '18%' },
            { id: 'proctor', name: 'Proctors', width: '18%' },
            { id: 'start', name: 'Start', width: '8%' },
            { id: 'end', name: 'End', width: '8%' },
          ] as any}
          data={filteredRows as any}
        >
          {filteredRows.flatMap((r) => {
            const cid = r.course_id;
            const eventHref = buildEventDetailPath(cid, r.event_id, LEADER_RETURN_TO);
            const courseHref = buildCourseDetailPath(cid, { returnTo: LEADER_RETURN_TO });

            return [
              <div
                key={`e-${r.id}`}
                slot={`row-${r.id}-column-event`}
                className="min-h-10 min-w-0 flex items-center font-semibold truncate"
                title={r.title}
              >
                {eventHref ? (
                  <button
                    className="bg-transparent border-none p-0 m-0 text-link underline cursor-pointer font-inherit text-left truncate"
                    type="button"
                    onClick={() => navigate(eventHref)}
                  >
                    {r.title}
                  </button>
                ) : (
                  r.title
                )}
              </div>,

              <div
                key={`c-${r.id}`}
                slot={`row-${r.id}-column-course`}
                className="min-h-10 min-w-0 flex items-center truncate"
                title={r.course_label}
              >
                {courseHref ? (
                  <button
                    className="bg-transparent border-none p-0 m-0 text-link underline cursor-pointer font-inherit text-left truncate"
                    type="button"
                    onClick={() => navigate(courseHref)}
                  >
                    {r.course_label}
                  </button>
                ) : (
                  '—'
                )}
              </div>,

              <div
                key={`o-${r.id}`}
                slot={`row-${r.id}-column-owner`}
                className="min-h-10 flex items-center"
              >
                {r.owners_team.length ? (
                  <span style={pillStyle('info')}>{r.owners_team.join(', ')}</span>
                ) : (
                  '—'
                )}
              </div>,

              <div
                key={`p-${r.id}`}
                slot={`row-${r.id}-column-proctor`}
                className="min-h-10 flex items-center"
              >
                {r.proctors_team.length ? (
                  <span style={pillStyle('success')}>{r.proctors_team.join(', ')}</span>
                ) : (
                  '—'
                )}
              </div>,

              <div
                key={`s-${r.id}`}
                slot={`row-${r.id}-column-start`}
                className="min-h-10 flex items-center"
              >
                {r.event_date ?? '—'}
              </div>,

              <div
                key={`en-${r.id}`}
                slot={`row-${r.id}-column-end`}
                className="min-h-10 flex items-center"
              >
                {r.event_end_date ?? '—'}
              </div>,
            ];
          })}
        </HbrDomTable>
      </div>

      <HbrPagination
        itemCount={totalCount}
        pageSize={pageSize}
        page={pageIndex}
        advanced
        pageInfo
        sizeChanger
        pageOptions={PAGE_SIZE_OPTIONS}
        onHbr-size-changed={(e) => {
          setPageSize(e.detail.pageSize);
          setPageIndex(0);
        }}
        onHbr-page-changed={(e) => setPageIndex(e.detail)}
      />
    </div>
  );
}